import axios from 'axios';
import i18n from '@/shared/i18n';

export type ApiErrorDetails = {
  message: string;
  code?: string;
  data?: Record<string, unknown>;
};

const SAFE_REQUEST_ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;

let pendingSupportCode: { id: string; until: number } | null = null;

function headerValue(headers: unknown, name: string): string | undefined {
  if (!headers || typeof headers !== 'object') return undefined;
  const bag = headers as { get?: (headerName: string) => unknown } & Record<string, unknown>;
  const fromGet = typeof bag.get === 'function' ? bag.get(name) : undefined;
  const raw = fromGet ?? bag[name] ?? bag[name.toLowerCase()];
  if (Array.isArray(raw)) return typeof raw[0] === 'string' ? raw[0] : undefined;
  return typeof raw === 'string' ? raw : undefined;
}

/** Id từ response 5xx. Header phải được CORS expose thì trình duyệt mới đọc được. */
export function readRequestId(err: unknown): string | undefined {
  if (!axios.isAxiosError(err) || (err.response?.status ?? 0) < 500) return undefined;
  const raw = headerValue(err.response?.headers, 'x-request-id')?.trim();
  if (!raw || !SAFE_REQUEST_ID.test(raw)) return undefined;
  return raw;
}

export function withRequestId(message: string, err: unknown): string {
  const id = readRequestId(err);
  if (!id || message.includes(id)) return message;
  return `${message} ${i18n.t('common:supportCode', { id })}`;
}

/** Ghi nhận id để toast lỗi ngay sau đó gắn mã, kể cả khi copy không đọc body lỗi. */
export function noteServerRequestId(err: unknown): void {
  const id = readRequestId(err);
  if (!id) return;
  pendingSupportCode = { id, until: Date.now() + 1000 };
}

export function appendPendingSupportCode(message: string): { text: string; pinned: boolean } {
  const pending = pendingSupportCode;
  if (!pending || Date.now() > pending.until) {
    return { text: message, pinned: false };
  }
  pendingSupportCode = null;
  if (message.includes(pending.id)) return { text: message, pinned: true };
  return {
    text: `${message} ${i18n.t('common:supportCode', { id: pending.id })}`,
    pinned: true,
  };
}

export function getApiErrorDetails(err: unknown, fallback: string): ApiErrorDetails {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as
      | { message?: unknown; code?: unknown; data?: unknown }
      | undefined;
    const message =
      typeof body?.message === 'string' && body.message.trim()
        ? body.message
        : fallback;
    return {
      message: withRequestId(message, err),
      code: typeof body?.code === 'string' ? body.code : undefined,
      data:
        body?.data && typeof body.data === 'object' && !Array.isArray(body.data)
          ? (body.data as Record<string, unknown>)
          : undefined,
    };
  }
  if (err instanceof Error && err.message) {
    return { message: err.message };
  }
  return { message: fallback };
}

export function getApiErrorMessage(err: unknown, fallback: string): string {
  return getApiErrorDetails(err, fallback).message;
}

const AMBIGUOUS_TRANSPORT_CODES = new Set([
  'ECONNABORTED',
  'ETIMEDOUT',
  'ERR_CANCELED',
  'ERR_NETWORK',
]);

/** No HTTP response: timeout / drop. Server may still have committed. */
export function isAmbiguousTransportError(err: unknown): boolean {
  if (!axios.isAxiosError(err)) return false;
  if (err.response) return false;
  if (!err.code) return /timeout/i.test(err.message || '');
  return AMBIGUOUS_TRANSPORT_CODES.has(err.code);
}

export type RecoverIfMutationFinishedOptions = {
  /** Base delay between GET attempts (first three waits). Later waits use 2×. */
  intervalMs?: number;
  /** Stop polling and rethrow if the resource never reaches the target. */
  maxWaitMs?: number;
  /** Number of waits that use `intervalMs` before doubling. */
  fastWaitCount?: number;
};

const pageIsHidden = (): boolean =>
  typeof document !== 'undefined' && document.visibilityState === 'hidden';

/** Wait `ms` or until the tab is visible again / the deadline passes. */
export function waitForNextPoll(ms: number, deadline: number): Promise<void> {
  return new Promise((resolve) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let settled = false;

    const finish = (): void => {
      if (settled) return;
      settled = true;
      if (timer !== undefined) clearTimeout(timer);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
      resolve();
    };

    const onVisibility = (): void => {
      if (pageIsHidden()) return;
      finish();
    };

    const remaining = deadline - Date.now();
    if (remaining <= 0) {
      finish();
      return;
    }

    timer = setTimeout(finish, Math.min(ms, remaining));
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }
  });
}

/**
 * After a write with no response, poll GET until `isFinished` (server may still
 * be committing). HTTP 4xx/5xx are not recovered — only timeout/drop.
 * Otherwise rethrow the original transport error.
 */
export async function recoverIfMutationFinished<T>(
  err: unknown,
  load: () => Promise<T>,
  isFinished: (data: T) => boolean,
  options: RecoverIfMutationFinishedOptions = {}
): Promise<T> {
  if (!isAmbiguousTransportError(err)) throw err;
  const intervalMs = options.intervalMs ?? 1000;
  const maxWaitMs = options.maxWaitMs ?? 60_000;
  const fastWaitCount = options.fastWaitCount ?? 3;
  const deadline = Date.now() + maxWaitMs;
  let waits = 0;

  while (true) {
    try {
      const data = await load();
      if (isFinished(data)) return data;
    } catch {
      // GET may fail while the original write is still holding the process.
    }
    if (Date.now() >= deadline) break;
    waits += 1;
    const delay = waits <= fastWaitCount ? intervalMs : intervalMs * 2;
    await waitForNextPoll(delay, deadline);
  }
  throw err;
}

/** Prefer i18n for known API error codes; otherwise use server message / fallback. */
export function getApiErrorMessageByCode(
  err: unknown,
  fallback: string,
  codeMessages?: Record<string, string>
): string {
  const details = getApiErrorDetails(err, fallback);
  if (details.code && codeMessages?.[details.code]) {
    return withRequestId(codeMessages[details.code], err);
  }
  return details.message;
}
